import type { AssetType } from '@cartwhl/common/api/getAssets';

import { formatEditedAt } from '../Dashboard/workItemGrouping';
import type { AssetLibraryMediaSelection } from './assetLibraryMediaSelection';
import { toAssetLibraryMediaSelection } from './assetLibraryMediaSelection';
import type { AssetLibraryDisplayItem } from './utils';
import { isAssetVideoThumbnail } from './utils';

export type AssetLibraryMediaPreviewState = {
  open: boolean;
  title: string;
  mediaUrl: string | null;
  mediaType: 'image' | 'video';
  createdAt: string | null;
  createdBy: string | null;
  isBookmarked: boolean;
  showMainLibraryPreviewActions: boolean;
  mediaSelection: AssetLibraryMediaSelection | null;
  showOpenWithActions: boolean;
};

export const INITIAL_MEDIA_PREVIEW_STATE: AssetLibraryMediaPreviewState = {
  open: false,
  title: '',
  mediaUrl: null,
  mediaType: 'video',
  createdAt: null,
  createdBy: null,
  isBookmarked: false,
  showMainLibraryPreviewActions: false,
  mediaSelection: null,
  showOpenWithActions: false,
};

export function formatAssetCreatedLabel(createdAt: string): string {
  return `Created ${formatEditedAt(createdAt)}`;
}

export function resolveMediaPreviewType(
  assetType: AssetType,
  mediaUrl: string,
): 'image' | 'video' {
  return isAssetVideoThumbnail(assetType, mediaUrl) ? 'video' : 'image';
}

export function isMediaPreviewableAsset(
  asset: AssetLibraryDisplayItem,
): boolean {
  const mediaUrl = asset.thumbnailURL?.trim();
  if (!mediaUrl) {
    return false;
  }

  if (asset.assetType === 'MEDIA_IMAGE' || asset.assetType === 'MEDIA_VIDEO') {
    return true;
  }

  return false;
}

export function toMediaPreviewFromAsset(
  asset: AssetLibraryDisplayItem,
): Pick<
  AssetLibraryMediaPreviewState,
  | 'title'
  | 'mediaUrl'
  | 'mediaType'
  | 'createdAt'
  | 'createdBy'
  | 'isBookmarked'
  | 'mediaSelection'
> | null {
  if (!isMediaPreviewableAsset(asset)) {
    return null;
  }

  const mediaUrl = asset.thumbnailURL?.trim();
  if (!mediaUrl) {
    return null;
  }

  return {
    title: asset.assetName?.trim() || 'Untitled',
    mediaUrl,
    mediaType: resolveMediaPreviewType(asset.assetType, mediaUrl),
    createdAt: asset.createdAt ?? null,
    createdBy: asset.createdByUser || asset.createdBy || null,
    isBookmarked: Boolean(asset.isBookmarked),
    mediaSelection: toAssetLibraryMediaSelection(asset),
  };
}
